import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Truck, MapPin, Calendar, CheckCircle2, Clock, Plus, ArrowRight,
  Pencil, Lock, Send, Loader2, Route as RouteIcon, AlertCircle, XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";

function greet() {
  const h = new Date().getHours();
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}

function periodLabel() {
  const today = new Date();
  const day = today.getDate();
  let start, end;
  if (day >= 28) {
    start = new Date(today.getFullYear(), today.getMonth(), 28);
    end = new Date(today.getFullYear(), today.getMonth() + 1, 27);
  } else {
    start = new Date(today.getFullYear(), today.getMonth() - 1, 28);
    end = new Date(today.getFullYear(), today.getMonth(), 27);
  }
  const fmt = (d) => d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  return `${fmt(start)} até ${fmt(end)}`;
}

const statusInfo = {
  pendente: { color: "bg-amber-500/15 text-amber-300 border-amber-500/30", icon: Clock, label: "Pendente" },
  conferido: { color: "bg-green-500/15 text-green-300 border-green-500/30", icon: CheckCircle2, label: "Conferido" },
  rejeitado: { color: "bg-red-500/15 text-red-300 border-red-500/30", icon: XCircle, label: "Rejeitado" },
  correcao: { color: "bg-orange-500/15 text-orange-300 border-orange-500/30", icon: AlertCircle, label: "Correção" },
};

export default function DriverHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [day, setDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/closings/today")
      .then((r) => setDay(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function onFinalize() {
    const fd = new FormData();
    fd.append("date", day.date);
    setFinalizing(true);
    try {
      await api.post("/closings/finalize-day", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Fechamento do dia finalizado!");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setFinalizing(false);
    }
  }

  const routes = day?.routes || [];
  const summary = day?.summary;
  const isFinalized = day?.finalized;
  const allApproved = summary?.todos_conferidos;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400" data-testid="driver-greeting">
          {greet()},
        </p>
        <h1 className="mt-1 font-display text-3xl font-bold text-white" data-testid="driver-greeting-firstname">
          {user?.nome?.split(" ")[0] || "Motorista"}
        </h1>
      </div>

      <div className="rounded-2xl border border-slate-800 bg-[#131B2B] p-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
          Operação atual
        </p>
        <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
          <Info icon={Truck} label="Veículo" value={user?.veiculo || "—"} />
          <Info icon={Truck} label="Placa" value={user?.placa || "—"} />
          <Info icon={MapPin} label="Rota base" value={user?.rota || "—"} />
          <Info icon={Calendar} label="Período" value={periodLabel()} />
        </div>
      </div>

      {/* Day status */}
      {isFinalized ? (
        <div className="flex items-center gap-3 rounded-2xl border border-green-500/30 bg-green-500/10 p-4 text-green-200" data-testid="day-finalized-badge">
          <CheckCircle2 className="h-5 w-5 shrink-0" />
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wider opacity-80">Dia finalizado</p>
            <p className="font-display text-lg font-bold">
              {summary.rotas_count} rota{summary.rotas_count !== 1 ? "s" : ""} enviadas para conferência
            </p>
          </div>
        </div>
      ) : routes.length > 0 ? (
        <div className="flex items-center gap-3 rounded-2xl border border-blue-500/30 bg-blue-500/10 p-4 text-blue-200">
          <Clock className="h-5 w-5 shrink-0" />
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wider opacity-80">Em andamento</p>
            <p className="font-display text-lg font-bold">
              {routes.length} rota{routes.length !== 1 ? "s" : ""} registrada{routes.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-amber-200">
          <Clock className="h-5 w-5 shrink-0" />
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wider opacity-80">Status de hoje</p>
            <p className="font-display text-lg font-bold">Fechamento pendente</p>
          </div>
        </div>
      )}

      {/* Day summary */}
      {summary && routes.length > 0 && (
        <div className="rounded-2xl border border-slate-800 bg-[#131B2B] p-5" data-testid="day-summary">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-400">
              Resumo do dia · {new Date(day.date + "T00:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "long" })}
            </p>
            <span className="rounded-full bg-blue-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-blue-300">
              {summary.rotas_count} rota{summary.rotas_count !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3">
            <Metric label="Entregues" value={summary.entregues} accent="text-green-400" />
            <Metric label="Retornos" value={summary.retornos} accent="text-amber-300" />
            <Metric label="Fora Lista" value={summary.fora_lista} accent="text-red-300" />
          </div>
          <div className="mt-4 flex items-baseline justify-between border-t border-slate-800 pt-3">
            <span className="text-xs uppercase tracking-wider text-slate-500">Taxa de sucesso</span>
            <span className="font-display text-2xl font-bold text-blue-400" data-testid="day-sucesso">{summary.sucesso}%</span>
          </div>
        </div>
      )}

      {/* Routes list */}
      {routes.length > 0 && (
        <div className="space-y-3" data-testid="routes-list">
          {routes.map((r, idx) => {
            const s = statusInfo[r.status] || statusInfo.pendente;
            const Icon = s.icon;
            const editable = r.status !== "conferido";
            return (
              <div
                key={r.id}
                className={`rounded-2xl border border-slate-800 bg-[#131B2B] p-4 ${editable ? "cursor-pointer transition hover:border-blue-500/40" : "opacity-90"}`}
                onClick={() => editable && navigate(`/motorista/fechamento?id=${r.id}`)}
                data-testid={`route-card-${r.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/20 text-blue-300">
                      <RouteIcon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Rota {idx + 1}</p>
                      <p className="font-display text-base font-semibold text-white">{r.rota || "—"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {editable ? (
                      <Pencil className="h-3.5 w-3.5 text-blue-400" />
                    ) : (
                      <Lock className="h-3.5 w-3.5 text-green-400" />
                    )}
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${s.color}`}>
                      <Icon className="h-3 w-3" /> {s.label}
                    </span>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                  <Cell label="Entregues" value={r.entregues} accent="text-green-400" />
                  <Cell label="Retornos" value={r.retornos} accent="text-amber-300" />
                  <Cell label="Fora" value={r.fora_lista_qtd} accent="text-red-300" />
                  <Cell label="Sucesso" value={`${r.sucesso}%`} accent="text-blue-400" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Actions */}
      <div className="space-y-3">
        <button
          onClick={() => navigate("/motorista/fechamento")}
          disabled={isFinalized && allApproved}
          className="glow-primary group flex w-full items-center justify-between rounded-2xl bg-blue-600 px-6 py-5 text-left font-display text-lg font-bold text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid="driver-add-route-btn"
        >
          <span className="flex items-center gap-3">
            <Plus className="h-5 w-5" />
            {routes.length === 0 ? "Fazer fechamento" : "Adicionar nova rota"}
          </span>
          <ArrowRight className="h-5 w-5 transition group-hover:translate-x-1" />
        </button>

        {routes.length > 0 && !isFinalized && (
          <Button
            onClick={onFinalize}
            disabled={finalizing}
            data-testid="driver-finalize-day-btn"
            className="h-14 w-full rounded-2xl bg-green-600 text-base font-bold uppercase tracking-wider text-white hover:bg-green-500"
          >
            {finalizing ? <Loader2 className="h-5 w-5 animate-spin" /> : (
              <span className="inline-flex items-center gap-2"><Send className="h-5 w-5" /> Finalizar fechamento do dia</span>
            )}
          </Button>
        )}
      </div>

      {loading && routes.length === 0 && (
        <p className="text-center text-xs text-slate-500">Carregando…</p>
      )}
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 h-4 w-4 text-blue-400" />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
        <p className="truncate font-medium text-slate-100">{value}</p>
      </div>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`font-display text-2xl font-bold ${accent}`}>{value ?? 0}</p>
    </div>
  );
}

function Cell({ label, value, accent }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`font-display text-base font-bold ${accent}`}>{value ?? 0}</p>
    </div>
  );
}
